import React, { useEffect, useState } from 'react'
import {
  acquireBottomImmersive,
  releaseBottomImmersive,
} from '../utils/flutterStatusBar'
import TermsContent from './TermsContent'
import PrivacyContent from './PrivacyContent'
import Markdown from './Markdown'
import LoadingSpinner from './LoadingSpinner'
import { policyApi, PolicyKey } from '../api/policy'

type PolicyType = 'terms' | 'privacy' | 'marketing'

interface TermsModalProps {
  isOpen: boolean
  type: PolicyType
  onClose: () => void
}

const DEFAULT_TITLE: Record<PolicyType, string> = {
  terms: '이용약관',
  privacy: '개인정보처리방침',
  marketing: '마케팅 정보 수신 동의',
}

const TermsModal: React.FC<TermsModalProps> = ({ isOpen, type, onClose }) => {
  const [content, setContent] = useState<string | null>(null)
  const [title, setTitle] = useState<string>(DEFAULT_TITLE[type])
  const [loaded, setLoaded] = useState(false)

  // 센터 다이얼로그: 하단 SafeArea 해제 → dim이 홈 인디케이터까지 덮음
  useEffect(() => {
    if (!isOpen) return
    acquireBottomImmersive()
    return () => releaseBottomImmersive()
  }, [isOpen])

  // 열릴 때 관리자에서 편집한 최신 내용을 불러옴 (실패 시 하드코딩 fallback)
  useEffect(() => {
    if (!isOpen) return
    let alive = true
    setLoaded(false)
    setContent(null)
    setTitle(DEFAULT_TITLE[type])
    policyApi
      .getPolicy(type as PolicyKey)
      .then((p) => {
        if (!alive) return
        setContent(p.content || null)
        if (p.title) setTitle(p.title)
      })
      .catch(() => {
        if (alive) setContent(null)
      })
      .finally(() => {
        if (alive) setLoaded(true)
      })
    return () => {
      alive = false
    }
  }, [isOpen, type])

  if (!isOpen) return null

  const renderBody = () => {
    if (content) return <Markdown content={content} />
    if (!loaded) {
      return (
        <div className="w-full flex justify-center items-center py-40px">
          <LoadingSpinner size={28} borderWidth={3} />
        </div>
      )
    }
    // 로드 실패/빈 내용 → 하드코딩 fallback (마케팅은 하드코딩본 없음)
    if (type === 'terms') return <TermsContent />
    if (type === 'privacy') return <PrivacyContent />
    return (
      <p className="font-pretendard text-14px text-textColorText3">
        내용을 불러오지 못했습니다.
      </p>
    )
  }

  return (
    <>
      {/* 오버레이 */}
      <div
        className="fixed inset-0 bg-black bg-opacity-40 z-[1050]"
        onClick={onClose}
      />

      {/* 모달 컨테이너 */}
      <div className="fixed inset-0 z-[1050] flex items-center justify-center px-16px">
        <div
          className={`
            w-full
            max-w-[360px]
            max-h-[80vh]
            bg-white
            rounded-16px
            flex
            flex-col
            overflow-hidden
          `}
          onClick={(e) => e.stopPropagation()}
        >
          {/* 헤더 */}
          <div className="flex items-center justify-between p-16px border-b border-brandPrimary-10%">
            <h2 className="font-pretendard font-bold text-18px text-textColorText2">
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="w-24px h-24px flex items-center justify-center hover:opacity-70"
            >
              <img
                src="/images/close-icon.png"
                alt="닫기"
                className="w-20px h-20px object-contain"
              />
            </button>
          </div>

          {/* 내용 */}
          <div className="flex-1 overflow-y-auto scrollbar-hide p-16px">
            {renderBody()}
          </div>
        </div>
      </div>
    </>
  )
}

export default TermsModal
